import type {
	Activity,
	CollectionType,
	DataFields,
	IndicatorData,
	InteractionType,
	LearningTimeMode,
	SocialForm,
	TeachingModality,
	ToolCategories,
} from "@fola/schemas/content"
import type { BloomLevel } from "@fola/schemas/learningOutcome"
import type { FolaPhase } from "@fola/schemas/content"
import type { LiteralKeys } from "@fola/utils/types"

const _bloomLevel: LiteralKeys<BloomLevel> = {
	analyze: "Analyze",
	apply: "Apply",
	create: "Create",
	evaluate: "Evaluate",
	understand: "Understand",
	remember: "Remember",
}

const _folaPhase: LiteralKeys<FolaPhase> = {
	Design: "Design",
	Implement: "Implement",
	Evaluate: "Evaluate",
}

const _activities: LiteralKeys<Activity> = {
	imitation: "Imitation",
	reception: "Reception",
	excercising: "Exercising",
	exploration: "Exploration",
	experimentation: "Experimentation",
	creation: "Creation",
	selfReflexion: "Self-Reflexion",
	debate: "Debate",
}

const _interactions: LiteralKeys<InteractionType> = {
	materialStudent: "Material ➔ Student",
	studentMaterial: "Student ➔ Material",
	teacherStudent: "Teacher ➔ Student",
	studentTeacher: "Student ➔ Teacher",
	studentStudent: "Student ➔ Student",
	studentLearningEnvironment: "Student ➔ Learning Environment",
	learningEnvironmentStudent: "Learning Environment ➔ Student",
}

const _socialForms: LiteralKeys<SocialForm> = {
	individual: "Individual Work",
	partner: "Partner Work",
	group: "Team-based Group Work",
	plenum: "Plenum",
}

const _learningTimeModes: LiteralKeys<LearningTimeMode> = {
	asynchronous: "Asynchronous",
	synchronous: "Synchronous",
}

const _teachingModalities: LiteralKeys<TeachingModality> = {
	onSite: "On Site",
	offSite: "Off Site",
	online: "Online",
	hybrid: "Hybrid",
}

const _toolCategories: LiteralKeys<ToolCategories> = {
	quiz: "Quiz",
	AItools: "AI Tools",
	collaborativeWriting: "Collaborative Writing",
	forum: "Forum",
	wiki: "Wiki",
	authoringSystem: "Authoring System",
	audienceResponseSystem: "Audience Response System",
	antiPlagarismSoftware: "Anti Plagarism Software",
	audioVideoEditing: "Audio Video Editing",
	screenRecording: "Screen Recording",
	contentManagementSystem: "Content Management System",
	learningManagementSystem: "Learning Management System",
	ePortfolio: "ePortfolio",
	questionnairesAndEvaluationTools: "Questionnaires and Evaluation Tools",
	messenger: "Messenger",
	softwareDevelopmentTools: "Software Development Tools",
	videoConferencingSystem: "Video Conferencing System",
	collaborationBoard: "Collaboration Board",
	mobileLearning: "Mobile Learning",
	knowledgeManagement: "Knowledge Management",
	collaborativeDrawing: "Collaborative Drawing",
	onlineTestingSystem: "Online Testing System",
	conceptMappingTools: "Concept Mapping Tools",
	educationalResourcesTools: "Educational Resources Tools",
	interactiveLearningTools: "Interactive Learning Tools",
	videoManagementTools: "Video Management Tools",
	simulationAndImmersiveTechnologies: "Simulation and Immersive Technologies",
	collaborativeWritingTools: "Collaborative Writing Tools",
	visualisationTools: "Visualisation Tools",
	projectManagementTools: "Project Management Tools",
}

const _indicatorData: LiteralKeys<IndicatorData> = {
	readingComprehension: "Reading Comprehension",
	selfRefelxion: "Self-Reflexion",
	commitment: "Commitment",
	motivation: "Motivation",
}

const _dataFieldsData: LiteralKeys<DataFields> = {
	numberOfViews: "Number of Views",
	processingTime: "Processing Time",
	dateOfProcessing: "Date of Processing",
}

const _collectionNames: LiteralKeys<CollectionType> = {
	GU: "Goethe University Frankfurt/Main",
	OU: "Open Universiteit of the Netherlands",
	DIPF: "DIPF",
	own: "My Collection",
}

const _roles = {
	guest: "Guest",
	member: "Member",
	owner: "Owner",
}

const _memberStatus = {
	add: "Add as member",
	remove: "Remove member",
}

const strings = {
	// error: {
	// 	changeBySelfNotAllowed: "You do not have permission to change the admin status of this user.",
	// 	newPasswordMismatch: "The new passwords do not match.",
	// 	oldPasswordIncorrect: "The old password is incorrect.",
	// 	passwordChangeIncomplete: "To change the password, you have to fill out all three password fields.",
	// } as Record<LiteralKeys<UserErrorSchema["errors"]>, string>,
	_activities,
	_bloomLevel,
	_collectionNames,
	_dataFieldsData,
	_folaPhase,
	_indicatorData,
	_interactions,
	_learningTimeModes,
	_memberStatus,
	_roles,
	_socialForms,
	_teachingModalities,
	_toolCategories,
	abort: "Abort",
	activity: "Activity",
	activityType: "Activity Type",
	add: "Add",
	addActivity: "Add Activity",
	addInsight: "Add Insight",
	addLearningOutcome: "Add a new Learning Outcome",
	addLearningUnit: "Add Learning Unit",
	addMediatool: "Add Media or Tool",
	addNewLearningOutcome: "Add your own Learning Outcome",
	addNewLearningOutcomeFromCourse: "Add a new Learning Outcome from the Course",
	assessment: "Assessment",
	bloomsTaxonomy: "Bloom's Taxonomy",
	boardDescription: "Session Description",
	boardEditor: "Session Editor",
	boardTitle: "Session Title",
	cardTitle: "Card title",
	category: "Category",
	confirmDeleteBoard: (title: string) => `Are you sure you want to delete the board '${title}'?`,
	collection: "My Collection",
	connectedMembers: "more members are connected",
	copyLink: "Copy Link to Clipboard",
	createdAt: "Created at",
	createNew: "Create new",
	creationConfrontation: "Creation / Confrontation",
	currentRole: "Current Role",
	dataFields: "Data from the medium/tool",
	debateAnimation: "Debate / Animation",
	deleteBoard: "Delete Session",
	removeBoard: "Remove Session",
	description: "Description",
	descriptionInsight: "What do you want to know?",
	dropzone: "Drop a card here",
	enterBoard: "Enter Session",
	editActivity: "Edit Activity",
	editBoard: "Edit Session",
	editInsight: "Edit Insight",
	editLearningUnit: "Edit Learning Unit",
	editMediatool: "Edit Media or Tool",
	excercisingGuidance: "Exercising / Guidance",
	experimentationReactivity: "Experimentation / Reactivity",
	explorationDocumentation: "Exploration / Documentation",
	folaPhase: "FoLA Phase",
	getFromCourse: "Get from Course",
	imitationModeling: "Imitation / Modeling",
	indicatorData: "Indicators",
	indicators: "Indicators from the learning activity",
	insight: "Insight",
	interactionType: "Interaction Type",
	isOnline: "is online",
	membershipRequest: "Membership request",
	joinAsMember: "Do you want to join this session as a member?",
	joinAsMemberInquiry:
		"The owner of the session was informed. Once your request was accepted, you status will be changed to member.",
	informMemberInquiry: "A guest requested membership in your session:",
	learningOutcome: "Learning Outcome",
	learningOutcomes: "Learning Outcomes",
	learningTime: "Learning Time in minutes",
	learningTimeMode: "Learning Time Mode",
	learningUnit: "LearningUnit",
	learningUnitTitle: "LearningUnit title",
	mediatool: "Media or Tool",
	members: "Members",
	moveDownLearningUnit: "Move down Learning Unit",
	moveUpLearningUnit: "Move up Learning Unit",
	myMembership: "My Role",
	newDemoboard: "New Demoboard",
	newLearningOutcome: "New Learning Outcome",
	noLearningOutcomes: "No new Learning Outcomes for Session",
	noUsersOnline: "No users online",
	other: "Other",
	otherCategory: "Own Category",
	otherScenario: "Own Scenario",
	otherTool: "Own Medium or Tool",
	ownCategory: "Define your own Category",
	ownScenario: "Define your own Scenario",
	ownTool: "Define your own Medium or Tool",
	receptionTransmission: "Reception / Transmission",
	sandbox: "Sandbox",
	scenario: "Scenario",
	selectLearningOutcome: "Select a learning outcome",
	selfCoReflexion: "Self-reflexion / Co-reflexion",
	sessionDuration: "Session Duration in Minutes",
	socialForm: "Social Form",
	supervisionExpenditures: "Supervision Expenditures in minutes",
	tags: "Tags",
	teachingModality: "Teaching Modality",
	title: "Titel",
	titleRequiredForLearningOutcome: "To create a valid Learning Outcome, you have to fill out the title field.",
	tool: "Name of Media or Tool",
	updatedAt: "Zuletzt bearbeitet am",
	url: "URL",
	usersOnline: (n: number) => (n > 1 ? `${n} users online` : `${n} user online`),
	weight: "Weight",
}

export default strings
export type FolaBoardL10nStrings = typeof strings
